import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';

/**
 * The candidate's booking page (/i/:token/schedule): pick a time for the AI
 * interview, get a calendar invite. Booking is a reminder, not a gate - the
 * interview link works any time before it expires.
 */

interface SlotView {
  start: string;
  end: string;
  remaining: number;
}

interface ScheduleView {
  candidateFirstName: string;
  jobTitle: string;
  company: string;
  /** screening: the AI interview; assessment: the AI skills assessment; recruiter: the hiring team's interview. */
  stage?: 'screening' | 'assessment' | 'recruiter';
  minutes: number;
  timezone: string;
  expiresAt: string | null;
  joinUrl: string | null;
  booking: { start: string; end: string } | null;
  changesLeft: number;
  canBook: boolean;
  slots: SlotView[];
}

interface Booking {
  start: string;
  end: string;
  timezone: string;
  joinUrl: string | null;
  changesLeft: number;
}

const BRAND_GRADIENT = 'linear-gradient(135deg, #d6217f 0%, #a8279a 55%, #7e2ea6 100%)';
const HEADING = { fontFamily: '"Fraunces", Georgia, serif' } as const;
const FONTS_HREF = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Hanken+Grotesk:wght@400;500;600;700&display=swap';

function useBrandFonts(): void {
  useEffect(() => {
    if (document.querySelector(`link[href="${FONTS_HREF}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONTS_HREF;
    document.head.appendChild(link);
  }, []);
}

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(body.error ?? `Something went wrong (${res.status})`);
  return body;
}

function dayKey(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
}

function fmt(iso: string, timeZone: string, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('en-IN', { timeZone, ...opts }).format(new Date(iso));
}

function tzLabel(tz: string): string {
  return tz === 'Asia/Kolkata' ? 'IST' : tz.replace(/_/g, ' ');
}

export function Schedule() {
  useBrandFonts();
  const { token } = useParams() as { token: string };
  const [view, setView] = useState<ScheduleView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState<Booking | null>(null);
  const [changing, setChanging] = useState(false);
  const base = `/api/public/interview/${encodeURIComponent(token)}`;

  const load = useCallback(async () => {
    try {
      const v = await call<ScheduleView>(`${base}/slots?days=7`);
      setView(v);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  const tz = view?.timezone ?? 'Asia/Kolkata';
  const days = useMemo(() => {
    const map = new Map<string, SlotView[]>();
    for (const s of view?.slots ?? []) {
      const k = dayKey(s.start, tz);
      map.set(k, [...(map.get(k) ?? []), s]);
    }
    return [...map.entries()];
  }, [view, tz]);
  const activeDay = day && days.some(([k]) => k === day) ? day : (days[0]?.[0] ?? null);
  const daySlots = days.find(([k]) => k === activeDay)?.[1] ?? [];

  async function confirm() {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      const b = await call<Booking>(`${base}/book`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ slotStart: picked }),
      });
      setBooked(b);
      setChanging(false);
      setPicked(null);
      await load();
    } catch (err) {
      setError((err as Error).message);
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function cancelBooking() {
    if (!window.confirm('Cancel this booking? Your interview link keeps working until it expires.')) return;
    setBusy(true);
    try {
      await call(`${base}/cancel-booking`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
      setBooked(null);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const current: Booking | null =
    booked ?? (view?.booking ? { ...view.booking, timezone: tz, joinUrl: view.joinUrl, changesLeft: view.changesLeft } : null);
  const joinPath = `/i/${token}`;

  return (
    <div className="min-h-full px-4 py-10 text-[#221b33]" style={{ background: '#f6f4fb', fontFamily: '"Hanken Grotesk", ui-sans-serif, system-ui, sans-serif' }}>
      <div className="mx-auto max-w-2xl">
        <p className="text-sm font-semibold tracking-wide text-[#a8279a] uppercase">{view?.company ?? 'Eduvy'}</p>
        <h1 className="mt-1 text-3xl font-semibold sm:text-4xl" style={HEADING}>
          {current && !changing
            ? 'You are booked in'
            : view?.stage === 'assessment'
              ? 'Pick a time for your skills assessment'
              : view?.stage === 'recruiter'
                ? 'Pick a time for your interview with the hiring team'
                : 'Pick a time for your interview'}
        </h1>
        {view && (
          <p className="mt-2 text-[#5b536b]">
            {view.stage === 'recruiter' ? (
              <>
                Hi {view.candidateFirstName} — the {view.jobTitle} interview with {view.company}'s hiring team takes about {view.minutes} minutes, on
                video from a laptop or desktop. Times are in {tzLabel(tz)}. At your booked time, join from the link in your email.
              </>
            ) : (
              <>
                Hi {view.candidateFirstName} — the {view.jobTitle} {view.stage === 'assessment' ? 'skills assessment' : 'interview'} takes about{' '}
                {view.minutes} minutes with our AI {view.stage === 'assessment' ? 'assessor, on a laptop or desktop' : 'interviewer'}. Times are in{' '}
                {tzLabel(tz)}. You can also <Link to={joinPath} className="font-medium text-[#a8279a] underline">start right now</Link> if that suits you
                better.
              </>
            )}
          </p>
        )}

        {error && <div className="mt-6 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-800 ring-1 ring-rose-200">{error}</div>}
        {!view && !error && <p className="mt-10 text-[#5b536b]">Loading available times…</p>}

        {view && current && !changing && (
          <div className="mt-8 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-[#e7e2f1]">
            <p className="text-sm text-[#5b536b]">Your interview</p>
            <p className="mt-1 text-2xl font-semibold" style={HEADING}>
              {fmt(current.start, tz, { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <p className="text-lg">
              {fmt(current.start, tz, { hour: 'numeric', minute: '2-digit' })} – {fmt(current.end, tz, { hour: 'numeric', minute: '2-digit' })} {tzLabel(tz)}
            </p>
            <p className="mt-3 text-sm text-[#5b536b]">We have emailed you a confirmation with a calendar invite, and will remind you a day before and 15 minutes before.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a
                href={`${base}/booking.ics`}
                download="interview.ics"
                className="rounded-full px-5 py-2.5 font-semibold text-white shadow-sm"
                style={{ backgroundImage: BRAND_GRADIENT }}
              >
                Add to calendar
              </a>
              <Link to={joinPath} className="rounded-full bg-white px-5 py-2.5 font-semibold text-[#7e2ea6] ring-1 ring-[#d9cfe9] hover:bg-[#faf8fd]">
                Join link
              </Link>
            </div>
            {current.joinUrl && <p className="mt-4 text-xs break-all text-[#8a829a]">{current.joinUrl}</p>}
            {view.canBook && (
              <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-[#efeaf6] pt-4 text-sm">
                {current.changesLeft > 0 ? (
                  <>
                    <button type="button" className="font-medium text-[#a8279a] hover:underline" onClick={() => setChanging(true)} disabled={busy}>
                      Choose another time
                    </button>
                    <button type="button" className="text-[#5b536b] hover:underline" onClick={cancelBooking} disabled={busy}>
                      Cancel booking
                    </button>
                    <span className="text-[#8a829a]">
                      {current.changesLeft} change{current.changesLeft === 1 ? '' : 's'} left
                    </span>
                  </>
                ) : (
                  <span className="text-[#8a829a]">This booking can no longer be changed.</span>
                )}
              </div>
            )}
          </div>
        )}

        {view && (!current || changing) && (
          <div className="mt-8">
            {!view.canBook && <p className="text-[#5b536b]">This interview has already started or finished, so there is nothing to book.</p>}
            {view.canBook && days.length === 0 && (
              <div className="rounded-2xl bg-white p-6 ring-1 ring-[#e7e2f1]">
                <p className="font-medium">No times are open before your link expires.</p>
                <p className="mt-1 text-sm text-[#5b536b]">
                  {view.stage === 'recruiter' ? (
                    'Please reply to the invitation email and the hiring team will find a time with you.'
                  ) : (
                    <>
                      You can still <Link to={joinPath} className="text-[#a8279a] underline">take it now</Link>.
                    </>
                  )}
                </p>
              </div>
            )}
            {days.length > 0 && (
              <>
                <div className="-mx-1 flex gap-2 overflow-x-auto pb-2" role="tablist">
                  {days.map(([k, slots]) => {
                    const on = k === activeDay;
                    return (
                      <button
                        key={k}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        onClick={() => {
                          setDay(k);
                          setPicked(null);
                        }}
                        className={`min-w-[5.5rem] shrink-0 rounded-2xl px-3 py-2.5 text-center ring-1 transition ${
                          on ? 'bg-[#221b33] text-white ring-[#221b33]' : 'bg-white text-[#221b33] ring-[#e7e2f1] hover:ring-[#a8279a]'
                        }`}
                      >
                        <span className="block text-xs uppercase opacity-75">{fmt(slots[0]!.start, tz, { weekday: 'short' })}</span>
                        <span className="block text-lg font-semibold" style={HEADING}>
                          {fmt(slots[0]!.start, tz, { day: 'numeric' })}
                        </span>
                        <span className="block text-xs opacity-75">{fmt(slots[0]!.start, tz, { month: 'short' })}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {daySlots.map((s) => {
                    const on = picked === s.start;
                    return (
                      <button
                        key={s.start}
                        type="button"
                        onClick={() => setPicked(s.start)}
                        className={`rounded-xl px-2 py-3 text-sm font-medium ring-1 transition ${
                          on ? 'text-white ring-transparent' : 'bg-white ring-[#e7e2f1] hover:ring-[#a8279a]'
                        }`}
                        style={on ? { backgroundImage: BRAND_GRADIENT } : undefined}
                      >
                        {fmt(s.start, tz, { hour: 'numeric', minute: '2-digit' })}
                      </button>
                    );
                  })}
                </div>
                <div className="sticky bottom-0 mt-6 flex flex-wrap items-center gap-3 bg-[#f6f4fb] py-3">
                  <button
                    type="button"
                    disabled={!picked || busy}
                    onClick={confirm}
                    className="rounded-full px-6 py-3 font-semibold text-white shadow-sm transition disabled:opacity-40"
                    style={{ backgroundImage: BRAND_GRADIENT }}
                  >
                    {busy ? 'Booking…' : picked ? `Confirm ${fmt(picked, tz, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}` : 'Choose a time'}
                  </button>
                  {changing && (
                    <button type="button" className="text-sm text-[#5b536b] hover:underline" onClick={() => setChanging(false)}>
                      Keep my current time
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {view?.expiresAt && (
          <p className="mt-10 text-xs text-[#8a829a]">
            Your interview link is valid until {fmt(view.expiresAt, tz, { dateStyle: 'medium', timeStyle: 'short' })} {tzLabel(tz)}.
          </p>
        )}
      </div>
    </div>
  );
}
